import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import { compose } from "redux";
import actionCreators from "@modules/service/actionCreator";
import * as serviceSelectors from "@modules/service/selectors";
import { Button, Pagination, Loader, GSGrid, View } from "@components";
import type { State } from "@store/reducers";
import type {
  AbsenceAuditLog,
  AuditLogsFilterType,
} from "@modules/service/reducer/audit-logs/types";
import { type PaginationExtendedType } from "@types";
import { route } from "@routes/helper";
import { AuditLogsFilter } from "@modules/shared/components";
import moment from "moment";

type PropsFromState = {
  isLoading: boolean,
  list: Array<AbsenceAuditLog>,
  pagination: PaginationExtendedType,
};

type PropsFromDispatch = {
  getAbsenceAuditLogs: typeof actionCreators.GetAbsenceAuditLogs,
  exportAbsenceAuditLogs: typeof actionCreators.ExportAbsenceAuditLogs,
};

type Props = {
  ...PropsFromState,
  ...PropsFromDispatch,
  ...PropsFromLocalize,
  clientId: number,
  showADDModal: boolean,
  showEDITModal: boolean,
};

export function AbsenceAuditLogs(props: Props) {
  const { list, pagination, clientId, t } = props;
  const [searchParams, setSearchParams]: [?AuditLogsFilterType, Function] = useState({
    clientId,
    search: {
      dateFrom: null,
      dateTo: null,
      userId: null,
      employeeId: null,
    },
    page: 1,
  });

  useEffect(() => {
    props.getAbsenceAuditLogs(searchParams);
  }, [clientId]);

  const columns = [
    {
      name: t("USER_ID"),
      key: "revisionId",
      align: "left",
      width: "fit-content",
      renderCell: ({ row }: Object) => row.userId,
    },
    {
      name: t("USER"),
      align: "left",
      key: "userFullName",
    },
    {
      name: t("ACTIVITY_DATE"),
      key: "revisionDate",
      width: "fit-content",
      renderCell: ({ row }: Object) => GSGrid.render.date(row.revisionDate),
    },
    {
      name: t("EMPLOYEE_ID"),
      key: "employeeId",
      align: "left",
      width: "fit-content",
    },
    {
      name: t("EMPLOYEE_RECORD"),
      key: "employeeFullName",
      align: "left",
    },
    {
      name: t("ABSENCE_ID"),
      key: "absenceId",
      width: "fit-content",
    },
    {
      name: t("ABSENCE_STATUS"),
      key: "absenceStatus",
      width: "fit-content",
      renderCell: ({ row }: Object) => GSGrid.render.statusPill(props.t(row.absenceStatus)),
    },
    {
      name: t("VIEW"),
      key: props.t("VIEW"),
      width: 40,
      align: "center",
      onCellClick: (row: Object) =>
        window.open(
          route("REPORTING_EMPLOYEE_ABSENCE_DETAILS_REVISION_PATH", {
            clientId,
            employeeId: row.employeeId,
            absenceId: row.absenceId,
            revisionId: row.revisionId,
          }),
          "_blank",
        ),
      renderCell: ({ row }: Object) => GSGrid.render.materialIcon("play_circle"),
    },
  ];

  function handleSubmitFilter(e: Event) {
    e.preventDefault();
    props.getAbsenceAuditLogs({ ...searchParams, page: 1 });
  }

  function handleFilterChange(data) {
    setSearchParams({
      ...searchParams,
      search: {
        ...data, // TODO - this ugly custom fix, remove it when the BE respect time zones +02:00
        dateFrom: moment(data.dateFrom)
          .set("hour", 0)
          .set("minute", 0)
          .set("second", 0)
          .utc()
          .toISOString(),
        dateTo: moment(data.dateTo)
          .set("hour", 23)
          .set("minute", 59)
          .set("second", 59)
          .utc()
          .toISOString(),
      },
    });
  }

  function handleExport(e: Event) {
    e.preventDefault();
    props.exportAbsenceAuditLogs(searchParams);
  }

  function handleChangePage(page: string) {
    const res = {
      ...searchParams,
      page: parseInt(page),
    };

    setSearchParams(res);
    props.getAbsenceAuditLogs(res);
  }

  return (
    <View>
      {props.isLoading && <Loader />}
      <View justifyContent="space-between" direction="row" margin={[0, 0, "1rem", 0]}>
        <View direction="row" gap={4} justifyContent="center" alignItems="flex-end">
          <AuditLogsFilter
            handleSubmit={handleSubmitFilter}
            onChange={handleFilterChange}
            data={searchParams.search}
          />

          <Button materialIcon="replay_circle_filled" onClick={handleSubmitFilter}>
            {props.t("SUBMIT")}
          </Button>
        </View>
        <View direction="row" justifyContent="center" alignItems="flex-end">
          <Button materialIcon="file_download" onClick={handleExport}>
            {t("EXPORT")}
          </Button>
        </View>
      </View>

      {list.length ? (
        <GSGrid columns={columns} rows={list} />
      ) : (
        <div className="my-5 d-flex justify-content-center">
          <h3 className="text-pinkish-grey">{t("ABSENCE_AUDITS_EMPTY")}</h3>
        </div>
      )}
      {pagination && (
        <Pagination
          className="my-3 d-flex flex-fill justify-content-center"
          currentPage={pagination.pageNumber}
          totalPages={pagination.totalPages}
          onChange={handleChangePage}
        />
      )}
    </View>
  );
}

const mapStateToProps = (state: State): PropsFromState => {
  return {
    list: serviceSelectors.selectAbsenceAuditLogsList(state),
    pagination: serviceSelectors.selectAbsenceAuditLogsPagination(state),
    isLoading: serviceSelectors.isLoadingAuditLogs(state),
  };
};

const mapDispatchToProps: PropsFromDispatch = {
  getAbsenceAuditLogs: actionCreators.GetAbsenceAuditLogs,
  exportAbsenceAuditLogs: actionCreators.ExportAbsenceAuditLogs,
};

export default compose(localize(), connect(mapStateToProps, mapDispatchToProps))(AbsenceAuditLogs);
